import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  Col,
  Dropdown,
  DropdownButton,
  Row,
} from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import {
  AuthedLayout,
  EditableTableDiv,
  HeaderDisplayKeysWithType,
} from '../../../components';
import { useClassGrade, useSemGrade } from '../../../hook';
import { Sight, StudentGradeNoSeat, UserInfo } from '../../../model';
import apis from '../../../apis';
import { SightContext } from '../../TableHeaders/SightHeader';
import {
  downloadDataAsExcel,
  printDataAsTable,
  SheetHeaderDisplay,
} from '../../../utils';
import { sightDiag } from '../../../utils/sight';
import { Nullable } from '../../../types';
import { toast } from 'react-toastify';
type PageData = StudentGradeNoSeat & Nullable<Sight> & { diag: string };
const formatSightValue = (value: unknown) => {
  if (typeof value !== 'number') return value as string;

  if (value === -1) return '<0.1';
  if (value < -1) return String(value);

  return Number.isInteger(value) ? value.toFixed(1) : String(value);
};
const tableHeader: (SheetHeaderDisplay<PageData> &
  HeaderDisplayKeysWithType<PageData>)[] = [
    { display: '座號', property: 'seat', style: { width: "5%" } },
    { display: '姓名', property: 'name' },
    {
      display: '裸視右',
      property: 'sight0R',
      onRender: (value) => formatSightValue(value),
      onSheetRender: (value) => formatSightValue(value),
    },
    {
      display: '裸視左',
      property: 'sight0L',
      onRender: (value) => formatSightValue(value),
      onSheetRender: (value) => formatSightValue(value),
    },
    {
      display: '戴鏡右',
      property: 'sightR',
      onRender: (value) => formatSightValue(value),
      onSheetRender: (value) => formatSightValue(value),
    },
    {
      display: '戴鏡左',
      property: 'sightL',
      onRender: (value) => formatSightValue(value),
      onSheetRender: (value) => formatSightValue(value),
    },
  ];

const mapState = (app: ApplicationState) => ({ ...app.auth });
const mapDispatch = ErrorDispatches;

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;

const sightClassUnCorrect: FunctionComponent<Props> = ({
  user,
  catchErrorForModal,
}) => {
  const { yearSem, element: semGradeElement } = useSemGrade();
  const { selectedClassGrade: classGrade, element: classGradeElement } =
    useClassGrade({
      yearSem,
    });
  const [students, setStudents] = useState<PageData[]>([]);
  const [userinfo, setUser] = useState<UserInfo>();
  useEffect(() => {
    if (yearSem && classGrade) {
      apis.getUserInfo()
        .then((u) => {
          setUser(u);
        })
        .catch(catchErrorForModal);
      toast
        .promise(
          apis
            .getStudentsSightUnCorrect(yearSem.year, yearSem.sem, classGrade.id)
            .then(r => {
              setStudents(
                r.map(({ sight, ...s }) => ({
                  ...sight,
                  ...s,
                  diag: sightDiag(sight),
                }))
              )
            }),
          {
            pending: '資料載入中......',
            success: '資料載入成功！',
            error: '資料載入失敗！請查看錯誤資訊。',
          }
        )
        .catch(catchErrorForModal);
    }
  }, [classGrade?.id, yearSem?.year, yearSem?.sem]);
  return (
    <AuthedLayout>
      <Row className="justify-content-between">
        <Col>
          <Row>
            <Col xs={3} className="mr-3">
              {semGradeElement}
            </Col>
            <Col xs={3} className="mr-3">
              {classGradeElement}
            </Col>
          </Row>
        </Col>
        <div className="d-flex">
          {/* 所有班級 */}
          <DropdownButton
            id="all-class-uncorrect-export"
            title="所有班級"
            variant="info"
            className="mr-3"
            disabled={!yearSem}
          >
            <Dropdown.Item
              onClick={() => {
                if (!yearSem) return;

                apis
                  .getStudentsSightUnCorrect(
                    yearSem.year,
                    yearSem.sem
                  )
                  .then((r) => {
                    const allStudents = r.map(
                      ({ sight, ...s }) => ({
                        ...sight,
                        ...s,
                        diag: sightDiag(sight),
                      })
                    );

                    downloadDataAsExcel({
                      title:
                        `${user.schools[user.currentSchool].name}` +
                        `${yearSem.year}學年` +
                        `${yearSem.sem}學期_裸眼視力不良清單`,
                      values: allStudents,
                      headers: tableHeader,
                      context: SightContext,
                      groupBy: (v) =>
                        `${v.grade}年${v.className}班`,
                      footer:
                        '承辦人: 組長: 主任: 校長:',
                      exportfile: userinfo?.exportFileType,
                      orientation: 'portrait',
                    });
                  })
                  .catch(catchErrorForModal);
              }}
            >
              Excel 下載
            </Dropdown.Item>

            <Dropdown.Item
              onClick={() => {
                if (!yearSem) return;

                apis
                  .getStudentsSightUnCorrect(
                    yearSem.year,
                    yearSem.sem
                  )
                  .then((r) => {
                    const allStudents = r.map(
                      ({ sight, ...s }) => ({
                        ...sight,
                        ...s,
                        diag: sightDiag(sight),
                      })
                    );

                    printDataAsTable({
                      title:
                        `${user.schools[user.currentSchool].name}` +
                        `${yearSem.year}學年` +
                        `${yearSem.sem}學期_裸眼視力不良清單`,
                      values: allStudents,
                      headers: tableHeader,
                      context: SightContext,
                      groupBy: (v) =>
                        `${v.grade}年${v.className}班`,
                      footer:
                        '承辦人: 組長: 主任: 校長:',
                      orientation: 'portrait',
                    });
                  })
                  .catch(catchErrorForModal);
              }}
            >
              列印
            </Dropdown.Item>
          </DropdownButton>

          {/* 單一班級 */}
          <DropdownButton
            id="single-class-uncorrect-export"
            title="單一班級"
            variant="success"
            disabled={!students.length}
          >
            <Dropdown.Item
              onClick={() => {
                downloadDataAsExcel({
                  title:
                    `${user.schools[user.currentSchool].name}` +
                    `${yearSem?.year}學年` +
                    `${yearSem?.sem}學期_裸眼視力不良清單`,
                  values: students,
                  headers: tableHeader,
                  context: SightContext,
                  groupBy: () =>
                    `${classGrade?.name ?? ''}`,
                  footer:
                    '承辦人: 組長: 主任: 校長:',
                  exportfile: userinfo?.exportFileType,
                  orientation: 'portrait',
                });
              }}
            >
              Excel 下載
            </Dropdown.Item>

            <Dropdown.Item
              onClick={() => {
                printDataAsTable({
                  title:
                    `${user.schools[user.currentSchool].name}` +
                    `${yearSem?.year}學年` +
                    `${yearSem?.sem}學期_裸眼視力不良清單`,
                  values: students,
                  headers: tableHeader,
                  context: SightContext,
                  groupBy: () =>
                    `${classGrade?.name ?? ''}`,
                  footer:
                    '承辦人: 組長: 主任: 校長:',
                  orientation: 'portrait',
                });
              }}
            >
              列印
            </Dropdown.Item>
          </DropdownButton>
        </div>
      </Row>
      <hr />
      <Row>
        <EditableTableDiv
          headers={tableHeader}
          values={students}
          context={SightContext}
        />
      </Row>
    </AuthedLayout>
  );
};

export const SightClassUnCorrect = connector(sightClassUnCorrect);
